export interface BranchGeocodeInput {
  address?: string;
  district?: string;
  city?: string;
  country?: string;
}

export interface BranchGeocodeResult {
  lat: number;
  lng: number;
  label: string;
}

const PHOTON_URL = 'https://photon.komoot.io/api/';
const NOMINATIM_URL = 'https://nominatim.openstreetmap.org/search';
const FETCH_TIMEOUT_MS = 7000;
const TURKEY_BOUNDS = { minLat: 35.8, maxLat: 42.3, minLng: 25.6, maxLng: 44.8 };

const toGoogleMapsPointUrl = (lat: number, lng: number): string =>
  `https://www.google.com/maps?q=${lat.toFixed(6)},${lng.toFixed(6)}`;

const trimPart = (value?: string): string => String(value || '').replace(/\s+/g, ' ').trim();

const normalizeTr = (value?: string): string =>
  trimPart(value)
    .toLocaleLowerCase('tr')
    .replace(/ı/g, 'i')
    .replace(/ğ/g, 'g')
    .replace(/ü/g, 'u')
    .replace(/ş/g, 's')
    .replace(/ö/g, 'o')
    .replace(/ç/g, 'c');

const isInTurkey = (lat: number, lng: number): boolean =>
  lat >= TURKEY_BOUNDS.minLat &&
  lat <= TURKEY_BOUNDS.maxLat &&
  lng >= TURKEY_BOUNDS.minLng &&
  lng <= TURKEY_BOUNDS.maxLng;

const CITY_BIAS: Record<string, { lat: number; lng: number }> = {
  istanbul: { lat: 41.0082, lng: 28.9784 },
  ankara: { lat: 39.9334, lng: 32.8597 },
  izmir: { lat: 38.4192, lng: 27.1287 },
  bursa: { lat: 40.195, lng: 29.06 },
  antalya: { lat: 36.8969, lng: 30.7133 },
  adana: { lat: 37.0, lng: 35.3213 },
  konya: { lat: 37.8746, lng: 32.4932 },
  gaziantep: { lat: 37.0662, lng: 37.3833 },
  kocaeli: { lat: 40.7667, lng: 29.9167 },
  mersin: { lat: 36.8121, lng: 34.6415 },
  sakarya: { lat: 40.7569, lng: 30.3781 },
  adapazari: { lat: 40.7569, lng: 30.3781 },
  duzce: { lat: 40.8438, lng: 31.1565 },
  bolu: { lat: 40.7395, lng: 31.6116 },
  mugla: { lat: 37.2153, lng: 28.3636 },
  sivas: { lat: 39.7477, lng: 37.0179 },
  yalova: { lat: 40.655, lng: 29.2769 },
  kayseri: { lat: 38.7312, lng: 35.4787 },
  eskisehir: { lat: 39.7767, lng: 30.5206 },
  denizli: { lat: 37.7765, lng: 29.0864 },
  samsun: { lat: 41.2867, lng: 36.33 },
};

const resolveCityBias = (city?: string): { lat: number; lng: number } =>
  CITY_BIAS[normalizeTr(city)] || { lat: 39.0, lng: 35.0 };

const matchesCity = (label: string, city?: string): boolean => {
  const cityNorm = normalizeTr(city);
  if (!cityNorm) return true;
  return normalizeTr(label).includes(cityNorm);
};

/**
 * Kat/daire/no gibi geocode'u bozan parçaları çıkarır.
 * "Hacıoğlu Mah. Atatürk Cad. No:15 Kat:2" → "Hacıoğlu Mah. Atatürk Cad."
 */
const cleanAddress = (raw?: string): string => {
  let value = trimPart(raw);
  if (!value) return '';
  value = value
    .replace(/\b(?:no|numara|nu|nr)[:.\s]*\d+[a-zA-Z]?([\/-]\d+[a-zA-Z]?)?/gi, ' ')
    .replace(/\b(?:kat|daire|blok|apt|apartman|kapı|kapi|iç\s*kapı|ic\s*kapi)[:.\s]*[\w/-]+/gi, ' ')
    .replace(/[.,;]+$/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  return value;
};

const uniqueNonEmpty = (values: string[]): string[] => {
  const seen = new Set<string>();
  const result: string[] = [];
  values.forEach((value) => {
    const key = normalizeTr(value);
    if (!key || seen.has(key)) return;
    seen.add(key);
    result.push(value);
  });
  return result;
};

export const buildGeocodeQueries = (input: BranchGeocodeInput, extraQuery?: string): string[] => {
  const address = cleanAddress(input.address);
  const street = address.split(',')[0]?.trim() || '';
  const district = trimPart(input.district);
  const city = trimPart(input.city);
  const country = trimPart(input.country) || 'Türkiye';
  const freeText = cleanAddress(extraQuery);

  return uniqueNonEmpty([
    freeText,
    [street, district, city, country].filter(Boolean).join(', '),
    [address, district, city, country].filter(Boolean).join(', '),
    [address, city, country].filter(Boolean).join(', '),
    [district, city, country].filter(Boolean).join(', '),
    [city, country].filter(Boolean).join(', '),
  ].filter((query) => query && normalizeTr(query) !== normalizeTr(country)));
};

const fetchJson = async (url: string): Promise<any | null> => {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  try {
    const response = await fetch(url, {
      signal: controller.signal,
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return null;
    return await response.json();
  } catch {
    return null;
  } finally {
    window.clearTimeout(timer);
  }
};

const toResult = (lat: number, lng: number, label: string): BranchGeocodeResult | null => {
  if (!Number.isFinite(lat) || !Number.isFinite(lng) || !isInTurkey(lat, lng)) return null;
  const safeLabel = trimPart(label);
  if (!safeLabel) return null;
  return { lat, lng, label: safeLabel };
};

const geocodePhoton = async (
  query: string,
  input: BranchGeocodeInput,
  limit = 5
): Promise<BranchGeocodeResult[]> => {
  const bias = resolveCityBias(input.city);
  const url =
    `${PHOTON_URL}?q=${encodeURIComponent(query)}` +
    `&limit=${limit}&lat=${bias.lat}&lon=${bias.lng}`;
  const data = await fetchJson(url);
  const features = Array.isArray(data?.features) ? data.features : [];
  const results: BranchGeocodeResult[] = [];

  features.forEach((feature: any) => {
    const coords = feature?.geometry?.coordinates;
    if (!Array.isArray(coords) || coords.length < 2) return;
    const lng = Number(coords[0]);
    const lat = Number(coords[1]);
    const props = feature?.properties || {};
    const label = [
      props.name,
      props.street,
      props.district || props.locality,
      props.city || props.county,
      props.state,
      props.country,
    ]
      .map((part: unknown) => trimPart(String(part || '')))
      .filter(Boolean)
      .filter((part: string, index: number, arr: string[]) => arr.indexOf(part) === index)
      .join(', ');
    const hit = toResult(lat, lng, label || String(props.name || query));
    if (hit && matchesCity(hit.label, input.city)) results.push(hit);
  });

  return results;
};

const geocodeNominatim = async (query: string, limit = 3): Promise<BranchGeocodeResult[]> => {
  const url =
    `${NOMINATIM_URL}?format=jsonv2&addressdetails=1&limit=${limit}` +
    `&countrycodes=tr&accept-language=tr&q=${encodeURIComponent(query)}`;
  const data = await fetchJson(url);
  const rows = Array.isArray(data) ? data : [];
  const results: BranchGeocodeResult[] = [];

  rows.forEach((hit: any) => {
    const item = toResult(Number(hit?.lat), Number(hit?.lon), String(hit?.display_name || ''));
    if (item) results.push(item);
  });

  return results;
};

const scoreResult = (item: BranchGeocodeResult, input: BranchGeocodeInput, extraQuery?: string): number => {
  const label = normalizeTr(item.label);
  let score = 0;
  const city = normalizeTr(input.city);
  const district = normalizeTr(input.district);
  if (city && label.includes(city)) score += 5;
  if (district && label.includes(district)) score += 4;
  const addressTokens = cleanAddress(input.address)
    .split(/[\s,]+/)
    .map((token) => normalizeTr(token))
    .filter((token) => token.length > 2);
  addressTokens.forEach((token) => {
    if (label.includes(token)) score += 2;
  });
  const queryTokens = trimPart(extraQuery)
    .split(/[\s,]+/)
    .map((token) => normalizeTr(token))
    .filter((token) => token.length > 2);
  queryTokens.forEach((token) => {
    if (label.includes(token)) score += 1;
  });
  return score;
};

const dedupeResults = (items: BranchGeocodeResult[]): BranchGeocodeResult[] => {
  const map = new Map<string, BranchGeocodeResult>();
  items.forEach((item) => {
    const key = `${item.lat.toFixed(5)}|${item.lng.toFixed(5)}`;
    if (!map.has(key)) map.set(key, item);
  });
  return Array.from(map.values());
};

export const geocodeSearch = async (
  input: BranchGeocodeInput,
  extraQuery?: string,
  limit = 8
): Promise<BranchGeocodeResult[]> => {
  const queries = buildGeocodeQueries(input, extraQuery);
  const collected: BranchGeocodeResult[] = [];

  // Photon hızlı; ilk sonuç veren sorguda dur. Nominatim yalnızca yedek.
  for (const query of queries) {
    const photonHits = await geocodePhoton(query, input, 6);
    if (photonHits.length > 0) {
      collected.push(...photonHits);
      break;
    }
  }

  if (collected.length === 0) {
    for (const query of queries.slice(0, 2)) {
      const nominatimHits = await geocodeNominatim(query, 3);
      const filtered = nominatimHits.filter((item) => matchesCity(item.label, input.city));
      if (filtered.length > 0) {
        collected.push(...filtered);
        break;
      }
    }
  }

  return dedupeResults(collected)
    .sort((a, b) => scoreResult(b, input, extraQuery) - scoreResult(a, input, extraQuery))
    .slice(0, limit);
};

export const geocodeBranchAddress = async (
  input: BranchGeocodeInput
): Promise<BranchGeocodeResult | null> => {
  const results = await geocodeSearch(input, undefined, 1);
  return results[0] || null;
};

export const geocodeBranchMapUrl = async (input: BranchGeocodeInput): Promise<string | null> => {
  const result = await geocodeBranchAddress(input);
  if (!result) return null;
  return toGoogleMapsPointUrl(result.lat, result.lng);
};
